/**
 * NavRail — shared 48px left rail for cross-page navigation.
 *
 * Cycle 101 Workspace-V2 Step 0, sprint-1 Task 1.3.
 * Port from output/design_reference/claude-design-2026-04-22-echelon/shared/navRail.jsx
 * into production React + TypeScript using --cd-* design tokens.
 *
 * Consumers: not yet mounted — sprint-2 wires it into the layout shell.
 * For now this file provides the component definition only; V1 surfaces
 * remain untouched until sprint-2's navigation pass.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

// ── Types ───────────────────────────────────────────────────────────────

export type NavPageId = 'workspace' | 'environment' | 'market';
export type Theme = 'dark' | 'light';

interface NavPage {
  id: NavPageId;
  label: string;
  to: string;
  desc: string;
  icon: React.ReactNode;
  shortcut: string;
}

interface NavFooterItem {
  id: string;
  label: string;
  desc: string;
  icon: React.ReactNode;
  disabled: true;
}

export interface NavRailProps {
  /** Which page is currently active (drives active indicator) */
  current?: NavPageId;
  /** Callback when the ⌘K button is clicked */
  onOpenCmdK?: () => void;
  /** Current theme — controls sun/moon icon */
  theme?: Theme;
  /** Callback to toggle theme */
  onThemeChange?: () => void;
  /** Optional additional class names */
  className?: string;
}

// ── Page registry ───────────────────────────────────────────────────────

const NAV_PAGES: NavPage[] = [
  {
    id: 'workspace',
    label: 'Workspace',
    to: '/workspace',
    desc: 'Real-time intelligence canvas',
    // Grid of 2×2 squares — evokes module canvas
    icon: (
      <g>
        <rect x="6" y="6" width="6" height="6" rx="0.5" />
        <rect x="14" y="6" width="6" height="6" rx="0.5" />
        <rect x="6" y="14" width="6" height="6" rx="0.5" />
        <rect x="14" y="14" width="6" height="6" rx="0.5" />
      </g>
    ),
    shortcut: '1',
  },
  {
    id: 'environment',
    label: 'Environment',
    to: '/environment',
    desc: 'Module graph · compose → attest',
    // Hexagon — evokes the composition node graph
    icon: (
      <g>
        <polygon points="13,4 20,8 20,18 13,22 6,18 6,8" fill="none" strokeWidth="1.4" />
        <circle cx="13" cy="13" r="2" fill="currentColor" stroke="none" />
      </g>
    ),
    shortcut: '2',
  },
  {
    id: 'market',
    label: 'Market',
    to: '/market',
    desc: 'Forecasts · forensics · feeds',
    // Concentric — evokes resolvable claims orbiting evidence
    icon: (
      <g>
        <circle cx="13" cy="13" r="9" fill="none" strokeWidth="1.4" />
        <circle cx="13" cy="13" r="5" fill="none" strokeWidth="1.4" />
        <circle cx="13" cy="13" r="1.6" fill="currentColor" stroke="none" />
      </g>
    ),
    shortcut: '3',
  },
];

const NAV_FOOTER: NavFooterItem[] = [];

// ── Rail kbd style (inline — matches design handoff) ────────────────────

const railKbdStyle: React.CSSProperties = {
  fontFamily: 'var(--cd-font-mono)',
  fontSize: 9,
  padding: '1px 4px',
  background: 'var(--cd-bg-inset)',
  border: '1px solid var(--cd-border)',
  borderRadius: 2,
  marginLeft: 6,
  color: 'var(--cd-fg-secondary)',
};

// ── Main component ──────────────────────────────────────────────────────

export function NavRail({
  current,
  onOpenCmdK,
  theme = 'dark',
  onThemeChange,
  className,
}: NavRailProps) {
  return (
    <aside
      role="navigation"
      aria-label="Primary navigation"
      className={cn('cycle-101-navrail', className)}
      style={{
        width: 48,
        flexShrink: 0,
        background: 'var(--cd-bg-panel)',
        borderRight: '1px solid var(--cd-border)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 10,
        paddingBottom: 10,
        zIndex: 30,
        fontFamily: 'var(--cd-font-sans)',
      }}
    >
      {/* Echelon mark */}
      <EchelonMark />

      <div style={{ width: 20, height: 1, background: 'var(--cd-border)', marginBottom: 8 }} />

      {/* Primary pages */}
      <nav
        aria-label="Primary pages"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
          alignItems: 'center',
        }}
      >
        {NAV_PAGES.map((p) => (
          <NavItem key={p.id} item={p} active={current === p.id} />
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      {/* Footer: future pages + cmdK + theme */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
          alignItems: 'center',
        }}
      >
        {NAV_FOOTER.map((p) => (
          <NavItem key={p.id} item={p} disabled />
        ))}

        <div style={{ width: 20, height: 1, background: 'var(--cd-border)', margin: '6px 0' }} />

        {onOpenCmdK && (
          <RailButton
            onClick={onOpenCmdK}
            title="Command palette"
            aria-label="Open command palette"
            tooltip={
              <>
                Command palette <kbd style={railKbdStyle}>⌘K</kbd>
              </>
            }
          >
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 9h6v6H9z" fill="none" stroke="currentColor" strokeWidth="1.4" />
              <path
                d="M9 9 V7 a2 2 0 0 1 -2 -2 a2 2 0 0 1 2 -2 a2 2 0 0 1 2 2 V9 M15 9 V7 a2 2 0 0 0 2 -2 a2 2 0 0 0 -2 -2 a2 2 0 0 0 -2 2 V9 M9 15 V17 a2 2 0 0 1 -2 2 a2 2 0 0 1 -2 -2 a2 2 0 0 1 2 -2 h2 M15 15 V17 a2 2 0 0 0 2 2 a2 2 0 0 0 2 -2 a2 2 0 0 0 -2 -2 h-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </RailButton>
        )}

        {onThemeChange && (
          <RailButton
            onClick={onThemeChange}
            title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            tooltip={theme === 'dark' ? 'Light theme' : 'Dark theme'}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </RailButton>
        )}
      </div>
    </aside>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────

function EchelonMark() {
  return (
    <Link
      to="/workspace"
      title="Echelon · Workspace"
      aria-label="Echelon home"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        marginBottom: 10,
        color: 'var(--cd-fg)',
        borderRadius: 'var(--cd-radius)',
      }}
    >
      {/* Minimal Echelon glyph — 4-quadrant mark (full SVG mark per design handoff is heavy;
          this keeps the nav-rail bundle small. Full mark can replace when icon assets ship.) */}
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          d="M4 4 L12 4 L12 8 L8 8 L8 12 L4 12 Z M20 4 L12 4 L12 8 L16 8 L16 12 L20 12 Z M4 20 L12 20 L12 16 L8 16 L8 12 L4 12 Z M20 20 L12 20 L12 16 L16 16 L16 12 L20 12 Z"
          fill="currentColor"
          opacity="0.92"
        />
      </svg>
    </Link>
  );
}

interface NavItemProps {
  item: NavPage | NavFooterItem;
  active?: boolean;
  disabled?: boolean;
}

function NavItem({ item, active, disabled }: NavItemProps) {
  const [hover, setHover] = useState(false);
  const color = active ? 'var(--cd-fg)' : disabled ? 'var(--cd-fg-dim)' : 'var(--cd-fg-muted)';
  const hoverColor = active || disabled ? color : 'var(--cd-fg)';

  const commonStyle: React.CSSProperties = {
    position: 'relative',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: hover && !disabled ? hoverColor : color,
    textDecoration: 'none',
    borderRadius: 'var(--cd-radius)',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'color .12s, background .12s',
    background:
      active || (hover && !disabled) ? 'var(--cd-bg-hover)' : 'transparent',
  };

  const innerContent = (
    <>
      {/* Active stripe */}
      {active && (
        <span
          style={{
            position: 'absolute',
            left: -6,
            top: 6,
            bottom: 6,
            width: 2,
            background: 'var(--cd-fg)',
            borderRadius: 2,
          }}
        />
      )}

      <svg
        viewBox="0 0 26 26"
        width="22"
        height="22"
        aria-hidden="true"
        stroke="currentColor"
        fill={active ? 'currentColor' : 'none'}
      >
        {item.icon}
      </svg>

      {/* Tooltip */}
      {hover && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            left: 46,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'var(--cd-bg-raised)',
            border: '1px solid var(--cd-border-strong)',
            padding: '6px 10px',
            borderRadius: 'var(--cd-radius)',
            whiteSpace: 'nowrap',
            boxShadow: 'var(--cd-shadow-2)',
            zIndex: 50,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--cd-fg)', fontWeight: 500 }}>{item.label}</span>
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 9.5,
              color: 'var(--cd-fg-muted)',
              letterSpacing: '0.06em',
            }}
          >
            {item.desc}
          </span>
          {'shortcut' in item && item.shortcut && (
            <kbd style={{ ...railKbdStyle, marginLeft: 2 }}>⌘{item.shortcut}</kbd>
          )}
        </div>
      )}
    </>
  );

  if (disabled) {
    return (
      <div
        role="presentation"
        aria-disabled="true"
        aria-label={`${item.label} (coming soon)`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={commonStyle}
      >
        {innerContent}
      </div>
    );
  }

  return (
    <Link
      to={(item as NavPage).to}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={commonStyle}
    >
      {innerContent}
    </Link>
  );
}

interface RailButtonProps {
  onClick?: () => void;
  title?: string;
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  'aria-label'?: string;
}

function RailButton({ onClick, title, tooltip, children, ...ariaProps }: RailButtonProps) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      {...ariaProps}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        position: 'relative',
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: hover ? 'var(--cd-fg)' : 'var(--cd-fg-muted)',
        borderRadius: 'var(--cd-radius)',
        transition: 'color .12s, background .12s',
        background: hover ? 'var(--cd-bg-hover)' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {children}
      {hover && tooltip && (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            left: 46,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'var(--cd-bg-raised)',
            border: '1px solid var(--cd-border-strong)',
            padding: '6px 10px',
            borderRadius: 'var(--cd-radius)',
            whiteSpace: 'nowrap',
            boxShadow: 'var(--cd-shadow-2)',
            zIndex: 50,
            pointerEvents: 'none',
            fontSize: 12,
            color: 'var(--cd-fg)',
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'var(--cd-font-sans)',
          }}
        >
          {tooltip}
        </div>
      )}
    </button>
  );
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" fill="currentColor" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <line
          key={a}
          x1="12"
          y1="3"
          x2="12"
          y2="6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          transform={`rotate(${a} 12 12)`}
        />
      ))}
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 14 A8 8 0 1 1 10 4 A6 6 0 0 0 20 14Z" fill="currentColor" />
    </svg>
  );
}

export { NAV_PAGES, NAV_FOOTER };
